import React, { useMemo, useState } from 'react';
import { ConveyorStationWithCost } from '../types';

interface CycleGanttProps {
    stations: ConveyorStationWithCost[];
    taktSeconds?: number | null;
    initialCycles?: number;
    showCyclesControl?: boolean;
    stationHues?: string[];
}

const DEFAULT_HUES = ['#e0461e', '#1e5f8f', '#4a6b3d', '#8a5a2b', '#6a4a7a'];

interface BaseStep {
    sIdx: number;
    stepIdx: number;
    id: string;
    text: string;
    duration: number;
    color: string;
    stationName: string;
    startInCycle: number;
}

interface GanttRow extends BaseStep {
    cycleIdx: number;
    start: number;
}

const fmtSec = (s: number): string => {
    if (Number.isInteger(s)) return `${s}`;
    return s.toFixed(2).replace(/\.?0+$/, '');
};

const CycleGantt: React.FC<CycleGanttProps> = ({
    stations,
    taktSeconds,
    initialCycles = 1,
    showCyclesControl = true,
    stationHues = DEFAULT_HUES,
}) => {
    const [cycles, setCycles] = useState<number>(initialCycles);

    const { baseSteps, totalCycle } = useMemo(() => {
        let cum = 0;
        const out: BaseStep[] = [];
        stations.forEach((station, sIdx) => {
            const color = stationHues[sIdx % stationHues.length];
            const stationName =
                station.name && station.name.trim()
                    ? station.name
                    : `Station ${sIdx + 1}`;
            (station.steps ?? []).forEach((step, stepIdx) => {
                out.push({
                    sIdx,
                    stepIdx,
                    id: step.id,
                    text: step.text,
                    duration: step.duration_seconds,
                    color,
                    stationName,
                    startInCycle: cum,
                });
                cum += step.duration_seconds;
            });
        });
        return { baseSteps: out, totalCycle: cum };
    }, [stations, stationHues]);

    const cyclePeriod =
        taktSeconds != null && taktSeconds > 0 ? taktSeconds : totalCycle;

    const rows: GanttRow[] = useMemo(() => {
        const out: GanttRow[] = [];
        for (let c = 0; c < cycles; c++) {
            for (const s of baseSteps) {
                out.push({
                    ...s,
                    cycleIdx: c,
                    start: c * cyclePeriod + s.startInCycle,
                });
            }
        }
        return out;
    }, [baseSteps, cycles, cyclePeriod]);

    if (baseSteps.length === 0) {
        return (
            <div className="p-4 ff-mono text-sm text-[#141414]/55 border border-[#141414]/15 bg-white/40">
                No operations to chart. Add steps in the conveyor configuration.
            </div>
        );
    }

    const stepsPerCycle = baseSteps.length;
    const span = Math.max(totalCycle, (cycles - 1) * cyclePeriod + totalCycle);

    // Logical SVG units
    const leftCol = 240;
    const rightPad = 16;
    const headerH = 32;
    const rowH = 22;
    const bandH = 18;
    const barArea = Math.max(420, Math.min(1500, span * 22));
    const pxPerSec = span > 0 ? barArea / span : 1;
    const cycleH = stepsPerCycle * rowH + bandH;
    const W = leftCol + barArea + rightPad;
    const H = headerH + cycles * cycleH + 6;

    const x = (sec: number) => leftCol + sec * pxPerSec;
    const yForCycleStart = (c: number) => headerH + c * cycleH;

    // Tick interval
    const tickStep = span > 120 ? 10 : span > 30 ? 5 : 1;
    const ticks: number[] = [];
    for (let t = 0; t <= span + 1e-6; t += tickStep) ticks.push(Math.round(t * 1000) / 1000);

    // Takt verticals (within span)
    const taktVerticals: number[] = [];
    if (taktSeconds != null && taktSeconds > 0) {
        for (let v = taktSeconds; v <= span + 1e-6; v += taktSeconds) {
            taktVerticals.push(v);
        }
    }

    return (
        <div>
            {showCyclesControl && (
                <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                    <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55">
                        Cycle Gantt
                    </div>
                    <div className="flex items-center gap-2 ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/70">
                        <span>Cycles</span>
                        <button
                            type="button"
                            onClick={() => setCycles((n) => Math.max(1, n - 1))}
                            className="w-6 h-6 border border-[#141414]/40 hover:bg-[#141414] hover:text-[#f3efe6]"
                            aria-label="Show one fewer cycle"
                        >
                            −
                        </button>
                        <span className="ff-mono text-sm w-6 text-center">{cycles}</span>
                        <button
                            type="button"
                            onClick={() => setCycles((n) => Math.min(8, n + 1))}
                            className="w-6 h-6 border border-[#141414]/40 hover:bg-[#141414] hover:text-[#f3efe6]"
                            aria-label="Show one more cycle"
                        >
                            +
                        </button>
                        <span className="text-[#141414]/45">
                            {taktSeconds != null && taktSeconds > 0
                                ? `· takt ${fmtSec(taktSeconds)}s`
                                : '· no takt set'}
                        </span>
                    </div>
                </div>
            )}

            <div className="border border-[#141414]/20 bg-white">
                <svg
                    viewBox={`0 0 ${W} ${H}`}
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-full h-auto block"
                    style={{ fontFamily: 'inherit' }}
                >
                    {/* vertical grid lines */}
                    {ticks.map((t, i) => (
                        <line
                            key={`grid-${i}`}
                            x1={x(t)}
                            x2={x(t)}
                            y1={headerH}
                            y2={H - 2}
                            stroke="#141414"
                            strokeOpacity={t === 0 || t === span ? 0.35 : 0.08}
                            strokeWidth={1}
                        />
                    ))}
                    {/* tick labels */}
                    {ticks.map((t, i) => (
                        <text
                            key={`label-${i}`}
                            x={x(t)}
                            y={headerH - 8}
                            fontSize={11}
                            textAnchor="middle"
                            fill="#141414"
                            fillOpacity={0.55}
                        >
                            {fmtSec(t)}
                        </text>
                    ))}
                    {/* takt verticals (red dashed) */}
                    {taktVerticals.map((v, i) => (
                        <line
                            key={`takt-${i}`}
                            x1={x(v)}
                            x2={x(v)}
                            y1={headerH - 4}
                            y2={H - 2}
                            stroke="#c2190a"
                            strokeWidth={1.5}
                            strokeDasharray="5 3"
                        />
                    ))}
                    {/* per-cycle yellow band */}
                    {Array.from({ length: cycles }, (_, c) => {
                        const startSec = c * cyclePeriod;
                        const endSec = startSec + totalCycle;
                        const bandY =
                            yForCycleStart(c) + stepsPerCycle * rowH + 2;
                        return (
                            <g key={`band-${c}`}>
                                <rect
                                    x={x(startSec)}
                                    y={bandY}
                                    width={Math.max(1, (endSec - startSec) * pxPerSec)}
                                    height={bandH - 4}
                                    fill="#f6e87a"
                                    stroke="#cdb633"
                                    strokeWidth={0.5}
                                />
                                <text
                                    x={(x(startSec) + x(endSec)) / 2}
                                    y={bandY + (bandH - 4) / 2 + 4}
                                    fontSize={11}
                                    textAnchor="middle"
                                    fill="#141414"
                                    fillOpacity={0.85}
                                >
                                    {fmtSec(totalCycle)}
                                </text>
                            </g>
                        );
                    })}
                    {/* rows */}
                    {rows.map((row, i) => {
                        const stepRow = i % stepsPerCycle;
                        const y = yForCycleStart(row.cycleIdx) + stepRow * rowH;
                        const barX = x(row.start);
                        const barW = Math.max(1, row.duration * pxPerSec);
                        return (
                            <g key={`row-${row.cycleIdx}-${row.id}`}>
                                {stepRow % 2 === 1 && (
                                    <rect
                                        x={0}
                                        y={y}
                                        width={W}
                                        height={rowH}
                                        fill="#141414"
                                        fillOpacity={0.025}
                                    />
                                )}
                                <text
                                    x={leftCol - 8}
                                    y={y + rowH / 2 + 4}
                                    fontSize={11}
                                    textAnchor="end"
                                    fill="#141414"
                                >
                                    {row.text}
                                </text>
                                <rect
                                    x={barX}
                                    y={y + 4}
                                    width={barW}
                                    height={rowH - 8}
                                    fill={row.color}
                                    fillOpacity={row.cycleIdx === 0 ? 0.95 : 0.6}
                                    stroke={row.color}
                                    strokeWidth={0.5}
                                />
                                {barW > 18 && (
                                    <text
                                        x={barX + 4}
                                        y={y + rowH / 2 + 4}
                                        fontSize={10}
                                        fill="white"
                                        fillOpacity={0.95}
                                    >
                                        {fmtSec(row.duration)}
                                    </text>
                                )}
                                <title>{`${row.stationName} · ${row.text} · ${fmtSec(
                                    row.duration
                                )}s (cycle ${row.cycleIdx + 1})`}</title>
                            </g>
                        );
                    })}
                    {/* cycle dividers */}
                    {Array.from({ length: cycles - 1 }, (_, c) => {
                        const yDiv = yForCycleStart(c + 1) - 1;
                        return (
                            <line
                                key={`div-${c}`}
                                x1={0}
                                x2={W}
                                y1={yDiv}
                                y2={yDiv}
                                stroke="#141414"
                                strokeOpacity={0.15}
                                strokeWidth={1}
                            />
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

export default CycleGantt;
